import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output, TemplateRef,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {DesignGroupPostElement} from '../../shared/design-group-post-element';
import {CardWebRestService} from '../../shared/card-web-rest.service';
import {map, startWith} from 'rxjs/operators';
import {CardWebListElement} from '../../shared/CardWebListElement';
import {MatDialog} from '@angular/material/dialog';
import {OnboardingService} from '../../graph-editor/shared/onboarding-stepper.service';
import {ShareDialogComponent} from '../share-dialog/share-dialog.component';
import {CommunityConnectorService} from '../../shared/community-connector.service';
import {CommunityMember} from '../../shared/CommunityMember';

@Component({
  selector: 'em-design-group',
  templateUrl: './design-group.component.html',
  styleUrl: './design-group.component.css'
})

export class DesignGroupComponent implements OnInit {
  @Input() designs: CardWebListElement[];
  @Input() designGroup: DesignGroupPostElement;
  @Input() options: Map<number, string>;
  @Input() groupId: number;
  @Input() cardDeckId: number;

  @Output() alteredDesign = new EventEmitter();

  @ViewChild('resetConfirmDialog') resetConfirmDialog: TemplateRef<any>;
  @ViewChild('noResetAllowedDialog') noResetAllowedDialog: TemplateRef<any>;
  @ViewChild('dataMigrationDialog') dataMigrationDialog: TemplateRef<any>;
  @ViewChild('askOnboardingTour') askOnboardingTour: TemplateRef<any>;
  @ViewChild('deleteDesignInvitationDialog') deleteDesignInvitationDialog: TemplateRef<any>;

  groupname = new FormControl('');
  filteredOptions: Observable<string[]>;

  dummyDesignName = '';

  addingNew = false;
  editingExistingDesign = new Map<string, boolean>();
  editingExistingGroup = new Map<number, boolean>();
  addForm: FormGroup;
  editGroupForm: FormGroup;
  shareDialogIsOpen = false;

  editingId: string | null = null;
  editForm: FormGroup;
  designOnboardingIsActive: boolean;

  avatarTemplateCommunityMemberMap = new Map<string, Observable<CommunityMember>>();

  constructor(
    private cwrs: CardWebRestService,
    private ccs: CommunityConnectorService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private obs: OnboardingService,
    private shareDialog: MatDialog
  ) {
    this.dummyDesignName = this.cwrs.getDummyDesignName();
    obs.getDesignOnboardingIsActive().subscribe(isActive => {
      this.designOnboardingIsActive = isActive;
    });
    this.addForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.maxLength(220)],
      groupname: ['']
    });
    this.editForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(220)],
      groupname: ['']
    });
    this.editGroupForm = this.fb.group({
      groupname: ['']
    });
  }

  ngOnInit() {
    this.filteredOptions = this.groupname.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
    this.designs.forEach( design => {
      if (design.designInvitationElement !== null) {
        const communityMember$ =  this.getAvatarByUserId(design.designInvitationElement.senderId);
        this.avatarTemplateCommunityMemberMap.set(design.designInvitationElement.senderAppId, communityMember$);
      }
    });
  }

  ngAfterViewInit() {
    if (this.designs.length === 1) {
      if (this.designs[0].name === this.cwrs.getDummyDesignName()) {
        this.informAboutDataMigration(this.designs[0]);
      }
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return Array.from(this.options.values()).filter(option => option.toLowerCase().includes(filterValue));
  }

  startAdd() {
    this.addingNew = true;
  }

  cancelAdd() {
    this.addingNew = false;
    this.addForm.reset();
  }

  submitAdd() {
    if (this.addForm.invalid) {
      return;
    }
    const {name, description, groupname} = this.addForm.value;
    const groupId = this.getGroupId(groupname);
    this.cwrs.createDesign({name, description, groupId, isDesignInvitation: false, timestamp: null})
      .subscribe((element) => {
        this.submitAlteredDesignEvent();
        this.cancelAdd();
      });
  }

  private submitAlteredDesignEvent() {
    this.alteredDesign.emit('alteredDesign');
  }

  private submitAlteredDesignGroupEvent() {
    this.alteredDesign.emit('alteredDesignGroup');
  }

  deleteDesign(design: CardWebListElement) {
    if (design.numberOfInvitations > 0) {
      const dialogRef = this.dialog.open(this.noResetAllowedDialog);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.openShareDialog(design, null, 1);
        }
      });
    }else{
      const dialogRef = this.dialog.open(this.resetConfirmDialog);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.cwrs.deleteDesign(design.name, design.groupId)
            .subscribe(() => this.submitAlteredDesignEvent());
        }
      });
    }
  }

  informAboutDataMigration(importDesign: CardWebListElement) {
    const dialogRef = this.dialog.open(this.dataMigrationDialog);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.startEdit(importDesign);
      }
    });
  }

  askAboutOnboardingTour() {
    const dialogRef = this.dialog.open(this.askOnboardingTour);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.obs.startDesignTour();
      }
    });
  }

  copyDesign(design: CardWebListElement) {
    let designOwnerUserId = null;
    if (design.designInvitationElement !== null) {
      designOwnerUserId = design.designInvitationElement.senderAppId;
    }
    this.cwrs.copyDesign(design.name, designOwnerUserId)
      .subscribe(() => this.submitAlteredDesignEvent());
  }

  public createIdFromCreatedDate(design: CardWebListElement) {
    // This differentiation is necessary to prevent opening the same editing dialog.
    if (design.designInvitationElement !== null) {
      return design.designInvitationElement.timestamp.toString();
    }else{
      return Date.parse(design.createdDateTime).toString();
    }
  }

  startEdit(design: CardWebListElement) {
    const timestamp = this.createIdFromCreatedDate(design);
    if (this.editingExistingDesign.size === 0 || !this.editingExistingDesign.get(timestamp)) {
      this.editingExistingDesign.clear();
      this.editingExistingDesign.set(this.createIdFromCreatedDate(design), true);
      this.editingId = design.name;
      let designName = decodeURIComponent(design.name);
      if (design.name === this.dummyDesignName) {
        designName = '';
      }
      this.editForm.patchValue({
        name: designName,
        description: design.description,
        groupname: this.options.get(design.groupId)
      });
      if (design.designInvitationElement !== null) {
        this.editForm.get('name').disable();
        this.editForm.get('description').disable();
      }else {
        this.editForm.get('name').enable();
        this.editForm.get('description').enable();
      }
    }
  }

  cancelEdit(design: CardWebListElement) {
    this.editingExistingDesign.set(this.createIdFromCreatedDate(design), false);
    this.editingId = null;
    this.editForm.reset();
  }

  saveEdit(design: CardWebListElement) {
    if (this.editForm.invalid) {
      return;
    }
    let name = this.editForm.get('name').value;
    let description = this.editForm.get('description').value;
    const groupname = this.editForm.get('groupname').value;
    if (design.designInvitationElement !== null) {
      name = this.decodeDesignName(design.name);
      description = design.description;
    }

    const groupId = this.getGroupId(groupname);
    const isDesignInvitation = design.designInvitationElement !== null;
    let timestamp = null;
    if (isDesignInvitation) {
      timestamp = design.designInvitationElement.timestamp;
    }
    this.cwrs
      .updateDesign(design.name, {name, description, groupId, isDesignInvitation, timestamp})
      .subscribe(() => {
        if (groupId !== design.groupId) {
          this.submitAlteredDesignGroupEvent();
        }
        this.submitAlteredDesignEvent();
        this.cancelEdit(design);
        if (design.name === this.dummyDesignName) {
          this.askAboutOnboardingTour();
        }
      });
  }

  private getGroupId(groupName: string) {
    if (groupName === undefined) {
      groupName = '';
    }
    groupName = groupName.trim();
    if (groupName === '') {
      return 0;
    }
    let groupId = 1;
    if (this.options.size > 0) {
      // groupId is counted always up - long time in future, this will hit the ceiling of number/integer
      // TODO: Add a consolidation function after e.g. 100000 to reuse gaps in number series
      groupId = Math.max(...this.options.keys()) + 1;
    }
    if (Array.from(this.options.values()).includes(groupName)) {
      this.options.forEach((value: string, key: number) => {
        if (value === groupName) {
          groupId = key;
        }
      });
    } else {
      this.cwrs.createDesignGroup({groupId, groupName}).subscribe((element) => {
        // this.loadDesignGroups();
      });
      this.options.set(groupId, groupName);
    }
    return groupId;
  }

  decodeDesignName(name: string) {
    return decodeURIComponent(name);
  }

  editDesign(design: CardWebListElement) {

  }

  getDesignGroups(designGroups: DesignGroupPostElement[]) {
    return designGroups;
  }

  startEditDesignGroup(designGroup: DesignGroupPostElement) {
    this.editingExistingGroup.set(designGroup.groupId, true);
    this.editGroupForm.patchValue({
      groupname: designGroup.groupName
    });
  }

  editDesignGroup($event: KeyboardEvent) {
    if ($event.code === 'Escape') {
      this.editingExistingGroup.set(this.designGroup.groupId, false);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    this.clearAllEdits();
  }

  private clearAllEdits() {
    this.editingExistingGroup.clear();
    this.editingExistingDesign.clear();
    this.addingNew = false;
  }

  saveDesignGroup(designGroup: DesignGroupPostElement) {
    if (this.editGroupForm.invalid) {
      return;
    }
    const {groupname} = this.editGroupForm.value;
    if (designGroup.groupName.trim() !== groupname.trim()) {
      designGroup.groupName = groupname;
      this.cwrs
        .updateDesignGroup(designGroup)
        .subscribe(() => {
          this.submitAlteredDesignGroupEvent();
          this.cancelGroupEdit();
        });
    }
  }

  cancelGroupEdit() {
    this.editingExistingGroup.clear();
  }

  designCanBeOpened(design: CardWebListElement) {
    return !this.designOnboardingIsActive
      && design.name !== this.cwrs.getDummyDesignName()
      && !this.editingExistingDesign.get(this.createIdFromCreatedDate(design));
  }

  public cancelDelete(): void {
    this.dialog.closeAll();
  }

  cancelImport() {
    this.dialog.closeAll();
  }

  cancelOnboarding() {
    this.dialog.closeAll();
  }

  openShareDialog(design: CardWebListElement, $event, selectedTabIndex: number) {
    this.shareDialogIsOpen = true;
    this.shareDialog.open(ShareDialogComponent,
      {
        data: {
          sharedDesign: design,
          selectedTab: selectedTabIndex
        },
        autoFocus: true
      });
    this.shareDialog.afterAllClosed.subscribe(result => {
      this.shareDialogIsOpen = false;
    });

  }

  getAvatarByUserId(userId: number) {
    return this.ccs.getAvatarByUserId(userId);
  }

  getAvatarTemplateUrl(avatarTemplate: string) {
    return this.ccs.getAvatarTemplateUrl(avatarTemplate);
  }

  getTooltipForInvitations(numberOfInvitations: number) {
    if (numberOfInvitations === 1) {
      return numberOfInvitations + ' Einladung verschickt';
    }else {
      return numberOfInvitations + '  Einladungen verschickt';
    }
  }

  deleteInvitation(design: CardWebListElement) {
    if (design.designInvitationElement !== null) {
      const dialogRef = this.dialog.open(this.deleteDesignInvitationDialog);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.ccs.deleteDesignInvitation(design.designInvitationElement)
            .subscribe(() => this.submitAlteredDesignEvent());
        }
      });
    }
  }

  getCommunityMessageUrl(postUrl: string) {
    return this.ccs.getCommunityMessageUrl(postUrl);
  }

  getTimestamp(design: CardWebListElement) {
    if (design.designInvitationElement === null) {
      return 'original';
    }else{
      return design.designInvitationElement.timestamp;
    }
  }
}
